import { useOne, useTranslation, useUpdate } from "@refinedev/core";
import { PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { get, isEmpty } from "lodash";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import Button from "components/button";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLogButton } from "components/historyLogButton";
import { isHaveActionPermission } from "utills/helpers";
import { getConfig } from "../config";
import { getListLabels } from "../labels";
import {
  DetailSections,
  ParticipantDetail,
  PickupParticipantKind,
  PickupParticipantVariant,
} from "../types";
import { getMessage } from "../utils";

const BibBadge = ({ value }: { value: string }) => (
  <div className="relative text-sm text-brand-secondary-500 px-3 py-1 w-fit bg-brand-secondary-400 rounded-[4px]">
    <div className="absolute size-2 left-4 -top-1 bg-white rounded-full" />
    {value}
    <div className="absolute size-2 left-4 -bottom-1 bg-white rounded-full" />
  </div>
);

const HistoryButton = ({
  resource,
  language,
  variant,
}: {
  resource: string;
  language?: string;
  variant: PickupParticipantVariant;
}) => {
  return (
    <div>
      <HistoryLogButton className="w-11 h-11" resource={resource} lang={variant === "wps" ? language : undefined} />
    </div>
  );
};

export const PickupParticipantDetailPage = ({
  kind,
  variant,
  sections,
}: {
  kind: PickupParticipantKind;
  variant: PickupParticipantVariant;
  sections: DetailSections;
}) => {
  const config = getConfig(kind, variant);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const labels = getListLabels(kind, variant, translate);
  const { id } = useParams();
  const { mutate, isLoading: isUpdating } = useUpdate();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<ParticipantDetail>({
    resource: config.detailResource,
    id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!id,
    } as any,
  });

  const participantDetailData = data?.data ?? null;
  const participantId = get(participantDetailData, "participant.participant_id", id ?? "");

  const { data: posData, refetch: refetchCheckPosData } = useOne<{
    pos_id: string;
    pos_name: string;
    pos_username: string;
  }>({
    resource: config.detailResource,
    id: `${participantId}/check-pos`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: kind === "sport" && !!participantDetailData,
    } as any,
  });

  const removePosCheckin = (posId: string) => {
    mutate(
      {
        resource: config.detailResource,
        id: `${participantId}/${posId}`,
        meta: {
          URLSuffix: "clear",
          ignoreResourceWrapper: true,
        },
        values: {},
      },
      {
        onSuccess: (res) => {
          toast.success(getMessage(get(res, "data.message"), language, labels.removePos));
          refetchCheckPosData();
        },
      },
    );
  };

  if (isLoading) return <FullScreenLoading />;

  const { InformationSection, RegistrationInformationSection, PickupInformationSection } = sections;

  return (
    <div className={variant === "wps" ? "w-full" : "w-full flex flex-col items-center gap-y-3 sm:gap-y-4"}>
      <Breadcrumbs
        type={variant === "wps" ? "wps" : undefined}
        customPageLabel={variant === "admin" ? labels.detailTitle : undefined}
        breadcrumbItems={
          variant === "wps"
            ? [
                { label: labels.checkIn },
                { label: labels.pageTitle, href: config.basePath },
                { label: labels.detailTitle, href: config.detailPath(participantId) },
              ]
            : undefined
        }
        disableBorder={variant === "wps" ? true : undefined}
        additionalLabel={
          participantDetailData ? (
            <div className="flex gap-x-2.5">
              <HistoryButton resource={config.historyResource(participantId)} language={language} variant={variant} />
              {kind === "sport" &&
              !isEmpty(posData?.data) &&
              isHaveActionPermission(config.permissionResource, config.permissionApp, "delete") ? (
                <Button
                  className="w-11 h-11 sm:w-auto bg-brand-secondary-500 border-brand-secondary-500 border-[1px] border-solid text-white hover:bg-brand-secondary-500 active:bg-brand-secondary-500 active:opacity-85"
                  onClick={() => removePosCheckin(posData?.data?.pos_id ?? "")}
                >
                  <span className="sr-only">{labels.removePos}</span>
                  <TrashIcon className="size-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">{labels.removePos}</span>
                </Button>
              ) : null}
              {isHaveActionPermission(config.permissionResource, config.permissionApp, "edit") ? (
                <Link to={config.editPath(participantId)}>
                  <Button className="w-11 h-11 sm:w-auto">
                    <span className="sr-only">{labels.edit}</span>
                    <PencilIcon className="w-4 h-4 text-white" />{" "}
                    <span className="hidden sm:block sm:ml-2">{labels.edit}</span>
                  </Button>
                </Link>
              ) : null}
            </div>
          ) : null
        }
      />
      {participantDetailData ? (
        <div className={variant === "wps" ? "w-full p-[16px] flex flex-col gap-[16px]" : "contents"}>
          {kind === "sport" ? (
            <div className="box-container">
              <div className="font-bold text-lg sm:text-xl flex flex-col gap-y-2 sm:gap-x-4 sm:flex-row sm:items-center">
                {labels.detailTitle}
                {get(participantDetailData, "participant.bib", "") ? (
                  <BibBadge value={get(participantDetailData, "participant.bib", "")} />
                ) : null}
              </div>
            </div>
          ) : null}
          <InformationSection participantDetailData={participantDetailData as any} />
          <RegistrationInformationSection participantDetailData={participantDetailData as any} />
          <PickupInformationSection participantDetailData={participantDetailData as any} />
        </div>
      ) : null}

      {(isFetching && isPreviousData) || isUpdating ? <FullScreenLoading /> : null}
    </div>
  );
};
